import EditOutlined from "@mui/icons-material/EditOutlined";
import { Box, IconButton, Tooltip } from "@mui/material";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import isNil from "lodash/isNil";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAddressHook } from "../../hooks/useAddressHook";
import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../redux/actions/loanDrilldownActions";
import AddressGeolocateInput from "../inputs/AddressGeolocateInput";
import Flx from "../layout/Flx";
import AddressMap from "./AddressMap";
import Txt from "../typography/Txt";
import BasicModal from "../modals/BasicModal";
import RffForm from "../finalForm/RffForm";
import RffAddressEditingField from "../finalForm/inputs/RffAddressEditingField";
import { formatAddressObjectFullAddress } from "../../_src_shared/utils/functions/formatAddressObjectFullAddress";
import InputWrapper from "../inputs/shared/InputWrapper";
import { addressLabel } from "../../_src_shared/utils/functions/addressLabel";

const LoanDrilldownAddressSection = ({
  property,
  borrower,
  name = "address",
  mapHeight = "220px",
}) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const {
    loading,
    updateBorrowerAddress,
    createBorrowerAddressFromObject,
    createSubjectPropertyFromObject,
  } = useAddressHook();
  const { updateSubjectProperty } = useUnderwritingHook();
  const [editing, setEditing] = useState(false);
  const [newAddress, setNewAddress] = useState(null);

  const targetObjectWithAddress = useMemo(() => {
    if (borrower) return { ...borrower };
    if (property) return { ...property };
  }, [borrower, property]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (targetObjectWithAddress?.[name]) {
      setNewAddress(null);
    }
  }, [targetObjectWithAddress, name]);

  const displayedFullAddress = useMemo(() => {
    return (
      addressLabel(targetObjectWithAddress?.[name]) || "No address available"
    );
  }, [targetObjectWithAddress, name]);

  const currentAddress = useMemo(() => {
    return targetObjectWithAddress?.[name];
  }, [targetObjectWithAddress, name]);

  const onRequestSuccess = useCallback(
    (res) => {
      dispatch(loanDrilldownSet(res));
      setEditing(false);
      setNewAddress(null);
    },
    [dispatch],
  );

  const handleUpdateAddress = async (rfAddress) => {
    if (!newAddress && !rfAddress?.address) {
      console.error("No new address to update");
      return;
    }

    const updatedAddress = rfAddress?.address || newAddress;
    if (borrower) {
      if (isNil(currentAddress)) {
        createBorrowerAddressFromObject({
          dealId: loanDrilldown?._id,
          borrowerId: borrower?._id,
          addressObject: updatedAddress,
          onSuccessFn: (d) => {
            onRequestSuccess(d);
          },
        });
        return;
      }

      updateBorrowerAddress({
        dealId: loanDrilldown?._id,
        borrowerId: borrower?._id,
        addressObject: updatedAddress,
        onSuccessFn: (d) => {
          onRequestSuccess(d);
        },
      });
    }

    if (property) {
      console.log("1: New address:", updatedAddress);
      if (isNil(currentAddress)) {
        createSubjectPropertyFromObject({
          dealId: loanDrilldown?._id,
          subjectPropertyId: property?._id,
          addressObject: updatedAddress,
          onSuccessFn: (d) => {
            onRequestSuccess(d);
          },
        });
        return;
      }
      console.log("2: Update address:", updatedAddress);

      /**
       Ex. Prev newAddress
        {
          "id": "LqQTGcK0fhM3tVZHJnnRTA-1775755398647",
          "fullAddress": "128 Carley Avenue, Huntington, NY 11743",
          "streetNumber": "128",
          "streetName": "Carley Avenue",
          "zip": "11743",
          "county": "NY",
          "state": "New York",
          "city": "Huntington",
          "country": "United States",
          "countryCode": "US",
          "latitude": 40.866524,
          "longitude": -73.435165,
          "coordinateTuple": [
              40.866524,
              -73.435165
          ],
          "score": 0.9816712086344217
        }
       */

      // return;
      updateSubjectProperty({
        loanId: loanDrilldown?._id,
        propertyId: property?._id,
        data: { address: updatedAddress },
        onSuccessFn: (d) => {
          onRequestSuccess(d);
        },
      });
    }
  };

  const onEditCancel = () => {
    setEditing(false);
    setNewAddress(null);
  };

  const onNewAddressSelect = (adr) => {
    setNewAddress(adr);
  };

  const handleEditClick = () => {
    setEditing(true);

    if (targetObjectWithAddress?.[name]) {
      setNewAddress(targetObjectWithAddress?.[name]);
    }
  };

  const styles = useMemo(() => {
    return {
      position: "absolute",
      width: "100%",
      height: mapHeight,
      top: 0,
      left: 0,
    };
  }, []);


  return (
    <Flx column>
      <Flx
        ac
        gap={1}
        sx={{
          px: 0.2,
          py: 0.5,
        }}
      >
        {editing ? (
          <>
            {/* */}
            {/* <AddressEditingRow
              onCancel={onEditCancel}
              loading={loading}
              address={property?.address || borrower?.primaryAddress}
              onSubmit={handleUpdateAddress}
            /> */}
            <AddressGeolocateInput
              value={newAddress}
              onChange={onNewAddressSelect}
              // sx={{ width: "100%" }}
              hideButtons
              keepInputVisible
              placeholder="Enter your address"
              fullWidth
            />
            <Flx end sx={{ p: 1, gap: 1 }}>
              <Button
                variant="outlined"
                onClick={onEditCancel}
                color="error"
                size="small"
              >
                Cancel
              </Button>
              <Button
                loading={loading}
                onClick={handleUpdateAddress}
                size="small"
                variant="contained"
                disabled={
                  !newAddress || newAddress === targetObjectWithAddress?.[name]
                }
              >
                Save Address
              </Button>
            </Flx>
          </>
        ) : (
          <Box
            onClick={handleEditClick}
            sx={{
              padding: "6px 8px",
              flexGrow: 1,
              background: "#e3f2fd35",
              border: "1px solid #e3f2fdff",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
            }}
          >
            <Txt
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#232a31",
              }}
            >
              {displayedFullAddress}
            </Txt>
            <EditLoanPropertyAddressButton property={property} />
          </Box>
        )}
      </Flx>
      <Card
        sx={{
          // width: "100%",
          // height: "100%",
          overflow: "hidden",
          p: 0,

          position: "relative",
          height: mapHeight,
          // width: "100%",
          flexGrow: 1,
          // ...styles,
        }}
      >
        <Box sx={styles}>
          {editing && !isNil(newAddress) ? (
            <AddressMap address={newAddress} height={mapHeight} sx={styles} />
          ) : (
            <AddressMap
              address={currentAddress}
              height={mapHeight}
              sx={styles}
            />
          )}
        </Box>
      </Card>
    </Flx>
  );
};

const EditLoanPropertyAddressButton = ({ property, borrower }) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <Tooltip title="Edit Address">
        <IconButton color="primary" onClick={() => setShow(true)} size="small">
          <EditOutlined className="thin" />
        </IconButton>
      </Tooltip>
      <BasicModal
        show={show}
        title={"Edit Property Address"}
        onClose={() => setShow(false)}
      >
        <InputWrapper label={"Current Address"}>
          {formatAddressObjectFullAddress(
            property?.address || borrower?.primaryAddress,
          )}
        </InputWrapper>
        <EditLoanPropertyAddressForm
          property={property}
          borrower={borrower}
          address={property?.address || borrower?.primaryAddress}
          onSuccessFn={() => setShow(false)}
        />
      </BasicModal>
    </>
  );
};



const EditLoanPropertyAddressForm = ({
  property,
  borrower,
  address,
  onSuccessFn,
}) => {
  console.log("EditLoanPropertyAddressForm", { property, borrower });
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const {
    loading,
    updateBorrowerAddress,
    createBorrowerAddressFromObject,
    createSubjectPropertyFromObject,
  } = useAddressHook();
  const { updateSubjectProperty } = useUnderwritingHook();
  const dispatch = useDispatch();


  const onRequestSuccess = useCallback(
    (d) => {
      dispatch(loanDrilldownSet(d));
      if (onSuccessFn) {
        onSuccessFn(d);
      }
    },
    [onSuccessFn],
  );

  const handleOnSubmit = useCallback(
    (val) => {
      const newAddress = val?.address;
      // const newAddress = val?.address || val?.primaryAddress;
      console.log("Submitting address:", newAddress);
      // return;
      // updateSubjectPropertyAddress({
      //   dealId: loanDrilldown?._id,
      //   subjectPropertyId: property?._id,
      //   addressObject: newAddress,
      //   onSuccessFn: (d) => {
      //     dispatch(loanDrilldownSet(d));
      //     if (onSuccessFn) {
      //       onSuccessFn(d);
      //     }
      //   },
      // });

      if (!newAddress) {
        console.error("No new address to update");
        return;
      }

      if (borrower) {
        // if (isNil(currentAddress)) {
        if (isNil(borrower?.primaryAddress)) {
          console.log("Create new borrower address", newAddress);
          return;
          createBorrowerAddressFromObject({
            dealId: loanDrilldown?._id,
            borrowerId: borrower?._id,
            addressObject: newAddress,
            onSuccessFn: (d) => {
              onRequestSuccess(d);
            },
          });
          return;
        }
        console.log("Update new borrower address", newAddress);
        return;
        updateBorrowerAddress({
          dealId: loanDrilldown?._id,
          borrowerId: borrower?._id,
          addressObject: newAddress,
          onSuccessFn: (d) => {
            onRequestSuccess(d);
          },
        });
        return;
      }

      if (property) {
        if (isNil(property?.address)) {
          console.log("Create new property address", newAddress);
          return;
          createSubjectPropertyFromObject({
            dealId: loanDrilldown?._id,
            subjectPropertyId: property?._id,
            addressObject: newAddress,
            onSuccessFn: (d) => {
              onRequestSuccess(d);
            },
          });
          return;
        }
        // console.log("Update new property address", newAddress);
        // return;
        updateSubjectProperty({
          loanId: loanDrilldown?._id,
          propertyId: property?._id,
          data: { address: newAddress },
          onSuccessFn: (d) => {
            onRequestSuccess(d);
          },
        });
      }
    },
    [
      property,
      borrower,
      loanDrilldown?._id,
      updateSubjectProperty,
      onRequestSuccess,
      dispatch,
    ],
  );

  return (
    <RffForm
      submitText={`Update Property Address`}
      formSpy
      // initialValues={initialValues}
      // initialValues={subjectPropertyId}
      onSubmit={handleOnSubmit}
      loading={loading}
    >
      {/* <RffAddressEditingField
        name={borrower ? "primaryAddress" : "address"}
        label="Address"
      /> */}
      <RffAddressEditingField name={"address"} label="Address" />
      {/* {borrower && (
        <RffAddressEditingField name={"primaryAddress"} label="Address" />
      )}
      {property && <RffAddressEditingField name={"address"} label="Address" />} */}
    </RffForm>
  );
};

export default LoanDrilldownAddressSection;
